import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { NotificationService } from '@setup/notifications//SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';
import { setDocumentTitle } from '@shared/functions/setDocumentTittle';

import { setNetworkLoading } from '../network/networkSlice';

import { setBreadcrumbsItems } from './commonSlice';

import type { AppDispatch } from '../store';
import type { DocumentsEntityTypeEnum } from '@shared/enums/documentEntityType.enum';
import type { FiltersUpdateArea } from '@shared/enums/filtersUpdateArea.enum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { CreditScoreDataModel } from '@shared/models/CreditScoreData.model';
import type { BreadcrumbsModel } from '@shared/models/breadcrumbs.model';
import type { HubsItemsModel } from '@shared/models/hubsItems.model';
import type { ItemUserModel } from '@shared/models/itemUser.model';

export const downloadMediaFile =
  (fileData: { token: string; isFile?: boolean; original_filename: string }) => async () => {
    try {
      const response = fileData.isFile
        ? await api.common.fileDownload(fileData.token)
        : await api.common.photoDownload(fileData.token);
      const imageURL = URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = imageURL;
      link.setAttribute('download', fileData.original_filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      NotificationService.error('Error downloading file');
    }
  };

export const uploadMediaFile =
  (
    formData: FormData,
    handleChangeProgress: (progress: number) => void,
    deleteLoadingMedia: (file?: any) => void,
  ) =>
  async () => {
    try {
      const response = await api.common.uploadFile(formData, handleChangeProgress);
      deleteLoadingMedia(response);
    } catch (e) {
      handleServerError(e);
      deleteLoadingMedia();
    }
  };

export const uploadMediaPhoto =
  (
    formData: FormData & { entity_type?: DocumentsEntityTypeEnum },
    handleChangeProgress: (progress: number) => void,
    deleteLoadingMedia: (file?: any) => void,
  ) =>
  async () => {
    try {
      const response = await api.common.uploadPhoto(formData, handleChangeProgress);
      deleteLoadingMedia(response);
    } catch (e) {
      handleServerError(e);
      deleteLoadingMedia();
    }
  };

export const deleteMedia = (token: string) => async () => {
  try {
    return await api.common.deleteMedia(token);
  } catch (e) {
    handleServerError(e);
    throw new Error('Error');
  }
};

export const updateFilters = createAsyncThunk<
  any,
  { filters: any; area: FiltersUpdateArea },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateFilters', async ({ filters, area }, { rejectWithValue }) => {
  try {
    const response = await api.common.updateFilters(filters, area);
    if (Array.isArray(response) && response?.length > 0) {
      return response[0].settings;
    } else {
      return [];
    }
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  }
});

export const getFilters = createAsyncThunk<
  any,
  { area: FiltersUpdateArea },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getFilters', async ({ area }, { rejectWithValue }) => {
  try {
    const response = await api.common.getFilters(area);
    if (Array.isArray(response) && response?.length > 0) {
      return response[0].settings;
    } else {
      return [];
    }
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  }
});
export const getCreditScoreUrl = createAsyncThunk<
  any,
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getCreditScoreUrl', async (_, { rejectWithValue }) => {
  try {
    return await api.common.getCreditScoreUrl();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  }
});

export const getCreditScoreInfo = createAsyncThunk<
  { score_data: CreditScoreDataModel },
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getCreditScoreInfo', async (_, { rejectWithValue }) => {
  try {
    return await api.common.getCreditScoreInfo();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  }
});

export const getFriends = createAsyncThunk<
  ItemUserModel[],
  { setLoading: (isLoading: boolean) => void | undefined } | undefined,
  { rejectValue: ErrorType }
>('getFriends', async (props, { rejectWithValue }) => {
  try {
    props?.setLoading?.(true);
    return await api.common.getFriends();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    props?.setLoading?.(false);
  }
});

export const getHubItems = createAsyncThunk<{ hubs: HubsItemsModel }, undefined, { rejectValue: ErrorType }>(
  'getHubItems',
  async (_, { rejectWithValue }) => {
    try {
      return await api.common.getHubItems();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getNetworkCounter = createAsyncThunk<
  { count: number },
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getNetworkCounter', async (_, { dispatch, rejectWithValue }) => {
  try {
    return await api.network.getCounter();
  } catch (e) {
    const axiosError = e as ServerErrorType;

    if (axiosError.status === StatusServerErrors.AuthorizationException) {
      return rejectWithValue(axiosError.data);
    } else {
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  } finally {
    dispatch(setNetworkLoading(false));
  }
});

export const setBreadcrumbs = createAsyncThunk<
  any,
  BreadcrumbsModel,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('setBreadcrumbs', async (data: BreadcrumbsModel, { dispatch }) => {
  const { title = 'Dashboard' } = data.slice(-1)[0] || {};
  setDocumentTitle(`${title} `);
  dispatch(setBreadcrumbsItems(data));
});

export const getOnlineUsers = createAsyncThunk<
  number[],
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getOnlineUsers', async (_, { dispatch, rejectWithValue }) => {
  try {
    return await api.network.getOnlineUsers();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setNetworkLoading(false));
  }
});

export const handleConformOauth = createAsyncThunk<
  any,
  { driver: string; code: string; isLogin: boolean },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('handleConformOauth', async ({ driver, code, isLogin }, { dispatch, rejectWithValue }) => {
  try {
    return await api.auth.callback(driver, code, isLogin);
  } catch (e: any) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);

    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setNetworkLoading(false));
  }
});

export const getFeedbackSurvey = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'getFeedbackSurvey',
  async (_, { rejectWithValue }) => {
    try {
      return await api.common.getFeedbackSurvey();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);
export const sendFeedbackSurvey = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'sendFeedbackSurvey',
  async (data, { rejectWithValue }) => {
    try {
      return await api.common.sendFeedbackSurvey(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);
